/**
 * In-flight GLB JSON-chunk sanitiser for Sirus M2→GLB exports.
 *
 * The Sirus extractor emits duplicate skeleton_root nodes — two separate
 * gltf nodes both list the same bone indices as children (multi-parented
 * hierarchy, a glTF spec violation). Babylon.js throws "Invalid recursive
 * node hierarchy" and refuses to load the mesh.
 *
 * Fix: parse the JSON chunk, detect multi-parented children, strip the
 * duplicate parent references, drop now-empty stray root nodes from
 * scenes[].nodes, and re-encode the JSON chunk with proper 4-byte padding.
 *
 * The binary chunk (BIN0 — textures, buffer data) is copied verbatim.
 * For GLBs that don't have the issue, returns a Blob URL wrapping the
 * original bytes (no alloc overhead beyond one Blob + URL creation).
 */

export async function sanitizeGlb(
  url: string,
): Promise<{ blobUrl: string; bytes: number; sanitized: boolean }> {
  // priority:'low' — model downloads yield the wire to terrain tiles/splats.
  const resp = await fetch(url, { priority: 'low' } as RequestInit);
  if (!resp.ok) throw new Error(`HTTP ${resp.status} fetching ${url}`);
  const ct = (resp.headers.get('content-type') ?? '').toLowerCase();
  if (ct.includes('text/html')) throw new Error(`SPA fallback HTML at ${url}`);
  const ab = await resp.arrayBuffer();
  const bytes = ab.byteLength;
  const dv = new DataView(ab);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (magic !== 'glTF') throw new Error(`not a GLB (magic=${magic})`);
  const jsonChunkLen = dv.getUint32(12, true);
  const jsonChunkType = dv.getUint32(16, true);
  if (jsonChunkType !== 0x4E4F534A /* 'JSON' */) throw new Error('first chunk not JSON');
  const jsonStart = 20;
  const jsonEnd = jsonStart + jsonChunkLen;
  const jsonBytes = new Uint8Array(ab, jsonStart, jsonChunkLen);
  let json: {
    nodes?: Array<{ name?: string; children?: number[] }>;
    scenes?: Array<{ nodes?: number[] }>;
    skins?: Array<{ skeleton?: number; joints?: number[] }>;
  };
  try {
    json = JSON.parse(new TextDecoder().decode(jsonBytes));
  } catch (e) {
    throw new Error(`malformed glTF JSON chunk: ${(e as Error).message}`);
  }

  type GlbNode = {
    name?: string;
    children?: number[];
    translation?: [number, number, number];
    rotation?: [number, number, number, number];
    scale?: [number, number, number];
    matrix?: number[];
    mesh?: unknown;
    skin?: unknown;
    camera?: unknown;
  };
  const nodes = (json.nodes ?? []) as GlbNode[];
  const parentsOf = new Map<number, number[]>();
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (!n?.children) continue;
    for (const c of n.children) {
      let arr = parentsOf.get(c);
      if (!arr) { arr = []; parentsOf.set(c, arr); }
      arr.push(i);
    }
  }
  const multiParented: number[] = [];
  for (const [c, parents] of parentsOf) if (parents.length > 1) multiParented.push(c);

  if (multiParented.length === 0) {
    const blob = new Blob([ab], { type: 'model/gltf-binary' });
    return { blobUrl: URL.createObjectURL(blob), bytes, sanitized: false };
  }

  // R232q86h: pick the parent that actually anchors the skeleton bind pose.
  // Old strategy «keep min-index OR skin.skeleton parent if matches» kept the
  // wrong root for Sirus M2→GLB exports where the canonical skeleton lives
  // under a non-canonical root (sometimes mesh root). Result: bones resolved
  // through a transform that didn't match bind pose → mesh torn into pieces
  // («ошмётки»). New scoring per parent:
  //   +5 if parent has identity TRS (no translation/rotation/scale set)
  //   +5 if parent name starts with 'skeleton_' / 'armature' / 'rig'
  //   +3 if parent IS skin.skeleton
  //   +3 if parent's transform-only siblings count > parent-without-transform
  //      siblings (heuristic: bone chain root usually has a transform-only
  //      parent).
  // Highest score wins. Ties broken by min(parents).
  const canonicalRoot = json.skins?.[0]?.skeleton;
  const isIdentity = (n: GlbNode | undefined): boolean => {
    if (!n) return false;
    if (n.matrix) return false; // explicit matrix => not identity by default
    const t = n.translation;
    const r = n.rotation;
    const s = n.scale;
    if (t && (t[0] || t[1] || t[2])) return false;
    if (r && (r[0] || r[1] || r[2] || (r[3] !== undefined && r[3] !== 1))) return false;
    if (s && (s[0] !== 1 || s[1] !== 1 || s[2] !== 1)) return false;
    return true;
  };
  const skeletonNameRe = /^(skeleton|armature|rig|root_bone|bone)/i;
  let removed = 0;
  for (const child of multiParented) {
    const parents = parentsOf.get(child)!;
    let bestParent = parents[0];
    let bestScore = -Infinity;
    for (const p of parents) {
      const node = nodes[p];
      let score = 0;
      if (isIdentity(node)) score += 5;
      if (node?.name && skeletonNameRe.test(node.name)) score += 5;
      if (typeof canonicalRoot === 'number' && p === canonicalRoot) score += 3;
      // Tie-breaker: prefer the parent with FEWER siblings (less likely to be
      // a generic mesh container).
      score -= (node?.children?.length ?? 0) * 0.01;
      if (score > bestScore || (score === bestScore && p < bestParent)) {
        bestScore = score;
        bestParent = p;
      }
    }
    for (const p of parents) {
      if (p === bestParent) continue;
      const node = nodes[p];
      if (!node?.children) continue;
      const before = node.children.length;
      node.children = node.children.filter((c) => c !== child);
      removed += before - node.children.length;
    }
  }

  if (json.scenes) {
    for (const scene of json.scenes) {
      if (!scene.nodes) continue;
      scene.nodes = scene.nodes.filter((idx) => {
        const node = nodes[idx];
        if (!node) return false;
        const n2 = node as { mesh?: unknown; camera?: unknown; skin?: unknown };
        const hasContent = n2.mesh !== undefined || n2.camera !== undefined || n2.skin !== undefined;
        const hasChildren = (node.children?.length ?? 0) > 0;
        return hasContent || hasChildren;
      });
    }
  }

  console.warn(`[glb-sanitizer] patched multi-parent in ${url}: ${multiParented.length} nodes, ${removed} refs removed`);

  const newJsonStr = JSON.stringify(json);
  const newJsonBytes = new TextEncoder().encode(newJsonStr);
  const padding = (4 - (newJsonBytes.length % 4)) % 4;
  const paddedJsonLen = newJsonBytes.length + padding;
  const paddedJson = new Uint8Array(paddedJsonLen);
  paddedJson.set(newJsonBytes, 0);
  for (let i = newJsonBytes.length; i < paddedJsonLen; i++) paddedJson[i] = 0x20;

  const binTailStart = jsonEnd;
  const binTailLen = ab.byteLength - binTailStart;
  const newTotalLen = 12 + 8 + paddedJsonLen + binTailLen;
  const out = new Uint8Array(newTotalLen);
  const outDv = new DataView(out.buffer);
  out[0] = 0x67; out[1] = 0x6C; out[2] = 0x54; out[3] = 0x46;
  outDv.setUint32(4, 2, true);
  outDv.setUint32(8, newTotalLen, true);
  outDv.setUint32(12, paddedJsonLen, true);
  outDv.setUint32(16, 0x4E4F534A, true);
  out.set(paddedJson, 20);
  out.set(new Uint8Array(ab, binTailStart, binTailLen), 20 + paddedJsonLen);

  const blob = new Blob([out], { type: 'model/gltf-binary' });
  return { blobUrl: URL.createObjectURL(blob), bytes, sanitized: true };
}
